<script setup lang="ts">
/**
 * /inventory -- Inventaire d'items du joueur (cf wiki §24.12).
 *
 * Liste les items possedes, groupes par kind (Artifact / Relic / Document /
 * Cle / Component / Scientifique / Ressource). Filtres : kind, rarete, search.
 * Fetch via GET /api/v1/me/items qui joint deja le catalogue ItemType
 * (Code / Label / Rarity / Description / IsTradable / ImageCode).
 *
 * Actions par item : V1 = passive (affichage). Les use-flows (consommer un
 * Document pour debloquer une recherche, ouvrir une anomalie avec une Cle,
 * exposer un Artifact au musee) sont les phases suivantes.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { extractApiError } from '@/api/client';
import { fetchMyItems, fetchMyShipComponents } from '@/api/me';
import { useItem } from '@/api/itemUse';
import { useSessionStore } from '@/stores/session.store';
import { useInventoryNewStore } from '@/stores/inventoryNew.store';
import type { MeItemEntry, ItemKind } from '@/types/inventory';
import {
  ITEM_KIND_LABELS,
  ITEM_KIND_TONE,
} from '@/types/inventory';
import type { MeShipComponentEntry } from '@/types/shipComponent';
import {
  SHIP_COMPONENT_CATEGORY_LABELS,
  SHIP_COMPONENT_CATEGORY_ORDER,
  shipComponentCategoryLabel,
  shipComponentToDisplayItem,
} from '@/types/shipComponent';
import { useAsyncLoad } from '@/composables/useAsyncLoad';
import { useListFilter } from '@/composables/useListFilter';

const session = useSessionStore();
const inventoryNew = useInventoryNewStore();

// useAsyncLoad : loading + try/catch + ElMessage.error centralises.
const { loading, run } = useAsyncLoad({ errorPrefix: 'Inventaire :' });
const items = ref<MeItemEntry[]>([]);
const shipComponents = ref<MeShipComponentEntry[]>([]);
const playerUniverseId = ref<number>(0);
const activeTab = ref<'items' | 'components'>('items');

// Filtres OBJETS (state local, pas de persistance V1).
const filterKind = ref<ItemKind | 'All'>('All');
const filterRarity = ref<number | 'All'>('All');
const searchQuery = ref('');

// Filtres COMPOSANTS : miroir des objets (recherche + type + rareté).
const filterCategory = ref<string>('All');
const compRarity = ref<number | 'All'>('All');
const compSearch = ref('');
// Basiques (type IsFixed) masqués par défaut : ce sont les composants de départ,
// toujours disponibles dans la palette du studio — inutiles à lister ici. Le
// joueur peut les révéler ponctuellement.
const showBasics = ref(false);

const KIND_ORDER: ItemKind[] = [
  'Relic',
  'Artifact',
  'ComponentPart',
  'Document',
  'Cle',
  'Scientifique',
  'RessourceConsommable',
];

async function load(): Promise<void> {
  await run(async () => {
    const uid = session.activeUniverseId ?? undefined;
    const [r, c] = await Promise.all([
      fetchMyItems(uid),
      fetchMyShipComponents(uid, showBasics.value),
    ]);
    items.value = r.items;
    playerUniverseId.value = r.playerUniverseId;
    shipComponents.value = c.components;
    // Synchronise le suivi des objets « nouveaux » (badge + compteur menu) sans
    // refetch : la liste possédée vient d'être chargée.
    inventoryNew.itemIds = r.items.map((i) => i.itemId);
  });
}

// Bascule « Afficher les basiques » : re-fetch des seuls composants (les basiques
// sont inclus/exclus côté serveur).
async function reloadComponents(): Promise<void> {
  const uid = session.activeUniverseId ?? undefined;
  const c = await fetchMyShipComponents(uid, showBasics.value);
  shipComponents.value = c.components;
}
watch(showBasics, () => { void reloadComponents(); });

onMounted(load);

// Filtrage composants (type + rareté + recherche), façon objets.
const { filtered: filteredComponents } = useListFilter(shipComponents, {
  predicates: {
    category: (c) => filterCategory.value === 'All' || c.category === filterCategory.value,
    rarity: (c) => compRarity.value === 'All' || c.rarity === compRarity.value,
  },
  search: () => compSearch.value,
  searchFields: (c) => [c.name, c.label, c.code],
});

// Catégories de composant réellement présentes dans l'inventaire (pour le filtre).
const componentCategories = computed<string[]>(() => {
  const present = new Set(shipComponents.value.map((c) => c.category));
  return SHIP_COMPONENT_CATEGORY_ORDER.filter((cat) => present.has(cat));
});

// Composants de vaisseau groupés par catégorie (ordre canonique), après filtrage.
const groupedComponents = computed<{ category: string; items: MeShipComponentEntry[] }[]>(() => {
  const byCat = new Map<string, MeShipComponentEntry[]>();
  for (const c of filteredComponents.value) {
    const arr = byCat.get(c.category) ?? [];
    arr.push(c);
    byCat.set(c.category, arr);
  }
  return SHIP_COMPONENT_CATEGORY_ORDER
    .map((cat) => ({ category: cat, items: byCat.get(cat) ?? [] }))
    .filter((g) => g.items.length > 0);
});

const componentCount = computed(() => shipComponents.value.length);

// Filtrage en cascade kind -> rarity -> search via useListFilter.
// La recherche est faite sur label + code + description (lowercase).
const { filtered } = useListFilter(items, {
  predicates: {
    kind: (it) => filterKind.value === 'All' || it.kind === filterKind.value,
    rarity: (it) => filterRarity.value === 'All' || it.rarity === filterRarity.value,
  },
  search: () => searchQuery.value,
  searchFields: (it) => [it.label, it.code, it.description],
});

// Groupement par kind apres filtrage. Ordre KIND_ORDER pour ranger
// Relic/Artifact d'abord (les plus "interessants" UX).
const groupedFiltered = computed<{ kind: ItemKind; items: MeItemEntry[] }[]>(() => {
  const byKind = new Map<ItemKind, MeItemEntry[]>();
  for (const it of filtered.value) {
    const arr = byKind.get(it.kind) ?? [];
    arr.push(it);
    byKind.set(it.kind, arr);
  }
  return KIND_ORDER
    .map(k => ({ kind: k, items: byKind.get(k) ?? [] }))
    .filter(g => g.items.length > 0);
});

// Stats globales pour le header (avant filtrage).
const totalCount = computed(() => items.value.length);
const totalQuantity = computed(() => items.value.reduce((s, it) => s + it.quantity, 0));
const totalByKind = computed<Map<ItemKind, number>>(() => {
  const m = new Map<ItemKind, number>();
  for (const it of items.value) m.set(it.kind, (m.get(it.kind) ?? 0) + 1);
  return m;
});

// =================================================================
// Consommation d'item (P10 + P17) -- Document / Cle / Scientifique
// =================================================================

/** Kinds qui ont un effet "use" cote backend. Les autres
 * (Artifact/Relic/ComponentPart) renverraient KIND_NOT_USABLE. */
const USABLE_KINDS = new Set<ItemKind>(['Document', 'Cle', 'Scientifique']);

function isUsable(it: MeItemEntry): boolean {
  return USABLE_KINDS.has(it.kind);
}

/** Message de confirmation par kind, pour clarifier au joueur ce
 * qu'il s'apprete a declencher. Pas de revert possible. */
function useConfirmText(it: MeItemEntry): { title: string; message: string } {
  switch (it.kind) {
    case 'Document':
      return {
        title: `Apprendre la recherche : ${it.label}`,
        message: it.description
          ? `Le document sera consomme et la recherche associee sera apprise (ou montera d'un niveau). ${it.description}`
          : 'Le document sera consomme et la recherche associee sera apprise (ou montera d\'un niveau).',
      };
    case 'Cle':
      return {
        title: `Utiliser la cle : ${it.label}`,
        message: 'La cle sera consommee. Le serveur renverra le code de cible deverrouille -- utilise-le ensuite sur l\'endpoint correspondant (anomalie / portail).',
      };
    case 'Scientifique':
      return {
        title: `Engager le scientifique : ${it.label}`,
        message: 'Le scientifique sera consomme (V1 placeholder -- mission espionnage future).',
      };
    default:
      return { title: `Consommer : ${it.label}`, message: 'Action ?' };
  }
}

const usingItemId = ref<number | null>(null);

async function onUse(it: MeItemEntry): Promise<void> {
  if (!isUsable(it) || !playerUniverseId.value) return;
  const { title, message } = useConfirmText(it);
  try {
    await ElMessageBox.confirm(message, title, {
      confirmButtonText: 'Consommer',
      cancelButtonText: 'Annuler',
      type: 'warning',
    });
  } catch {
    return; // user cancelled
  }
  usingItemId.value = it.itemId;
  try {
    const result = await useItem(it.itemId, { playerUniverseId: playerUniverseId.value });
    ElMessage.success(result.message);
    await load();
  } catch (err) {
    ElMessage.error(extractApiError(err) ?? 'Erreur a la consommation');
  } finally {
    usingItemId.value = null;
  }
}
</script>

<template>
  <div v-loading="loading" class="inventory-view">
    <!-- ============================ Header ============================== -->
    <header class="inv-header">
      <div>
        <h1 class="inv-title">Inventaire</h1>
        <p class="inv-subtitle text-muted">
          {{ totalCount }} item{{ totalCount > 1 ? 's' : '' }} differents
          · {{ totalQuantity }} unite{{ totalQuantity > 1 ? 's' : '' }} au total
        </p>
      </div>
      <div class="inv-kind-summary">
        <el-tag
          v-for="kind in KIND_ORDER"
          :key="kind"
          :type="ITEM_KIND_TONE[kind]"
          :effect="totalByKind.get(kind) ? 'dark' : 'plain'"
          size="small"
          class="inv-kind-chip"
          :class="{ 'is-empty': !totalByKind.get(kind) }"
        >
          {{ ITEM_KIND_LABELS[kind] }} : {{ totalByKind.get(kind) ?? 0 }}
        </el-tag>
      </div>
    </header>

    <el-tabs v-model="activeTab" class="inv-tabs">
    <el-tab-pane name="items" :label="`Objets (${totalCount})`">
    <div class="inv-pane">

    <!-- ============================ Filtres ============================= -->
    <section class="inv-filters">
      <el-input
        v-model="searchQuery"
        placeholder="Recherche : nom, code, description..."
        :prefix-icon="'Search'"
        clearable
        class="inv-filter-search"
      />
      <el-select v-model="filterKind" placeholder="Type d'objet" class="inv-filter-select">
        <el-option label="Tous les types d'objet" value="All" />
        <el-option
          v-for="kind in KIND_ORDER"
          :key="kind"
          :label="ITEM_KIND_LABELS[kind]"
          :value="kind"
        />
      </el-select>
      <el-select v-model="filterRarity" placeholder="Rareté" class="inv-filter-select">
        <el-option label="Toutes raretés" value="All" />
        <el-option label="Commun (0)" :value="0" />
        <el-option label="Rare (1)" :value="1" />
        <el-option label="Épique (2)" :value="2" />
        <el-option label="Légendaire (3)" :value="3" />
      </el-select>
    </section>

    <!-- =================== Zone défilante (objets) ====================== -->
    <div class="inv-scroll">
      <!-- Empty state -->
      <el-empty
        v-if="!loading && items.length === 0"
        description="Aucun item dans ton inventaire. Construis un site d'excavation et lance une fouille pour commencer."
      />
      <el-empty
        v-else-if="!loading && filtered.length === 0"
        description="Aucun item ne correspond à ces filtres."
      />

      <!-- Sections kind -->
      <section
        v-for="g in groupedFiltered"
        :key="g.kind"
        class="inv-section"
      >
        <h2 class="inv-section-title">
          <el-tag :type="ITEM_KIND_TONE[g.kind]" effect="dark" size="default">
            {{ ITEM_KIND_LABELS[g.kind] }}
          </el-tag>
          <span class="inv-section-count text-muted">
            {{ g.items.length }} entrée{{ g.items.length > 1 ? 's' : '' }}
          </span>
        </h2>

        <div class="inv-grid">
          <div
            v-for="it in g.items"
            :key="it.itemId"
            class="inv-item"
            @mouseenter="inventoryNew.markSeen(it.itemId)"
            @click="inventoryNew.markSeen(it.itemId)"
          >
            <span v-if="inventoryNew.isNew(it.itemId)" class="inv-new-badge">New</span>
            <ItemDisplay
              :item="it"
              size="medium"
              comparable
            >
              <!-- Bouton 'Consommer' pour Document/Cle/Scientifique (P17). -->
              <template v-if="isUsable(it)" #actions>
                <el-button
                  size="small"
                  type="primary"
                  plain
                  :loading="usingItemId === it.itemId"
                  @click="onUse(it)"
                >
                  <template v-if="it.kind === 'Document'">📖 Apprendre</template>
                  <template v-else-if="it.kind === 'Cle'">🔑 Utiliser</template>
                  <template v-else-if="it.kind === 'Scientifique'">🔬 Engager</template>
                  <template v-else>Consommer</template>
                </el-button>
              </template>
            </ItemDisplay>
          </div>
        </div>
      </section>
    </div>

    </div>
    </el-tab-pane>

    <!-- ===================== Composants de vaisseau ===================== -->
    <el-tab-pane name="components" :label="`Composants (${componentCount})`">
    <div class="inv-pane">

      <!-- ===================== Filtres composants ====================== -->
      <section class="inv-filters">
        <el-input
          v-model="compSearch"
          placeholder="Recherche : nom, code..."
          :prefix-icon="'Search'"
          clearable
          class="inv-filter-search"
        />
        <el-select v-model="filterCategory" placeholder="Type de composant" class="inv-filter-select">
          <el-option label="Tous les types de composants" value="All" />
          <el-option
            v-for="cat in componentCategories"
            :key="cat"
            :label="SHIP_COMPONENT_CATEGORY_LABELS[cat] ?? cat"
            :value="cat"
          />
        </el-select>
        <el-select v-model="compRarity" placeholder="Rareté" class="inv-filter-select">
          <el-option label="Toutes raretés" value="All" />
          <el-option label="Commun (0)" :value="0" />
          <el-option label="Rare (1)" :value="1" />
          <el-option label="Épique (2)" :value="2" />
          <el-option label="Légendaire (3)" :value="3" />
        </el-select>
        <!-- Basiques (composants de départ) masqués par défaut ; toujours dispos au studio. -->
        <el-switch
          v-model="showBasics"
          class="inv-filter-basics"
          active-text="Afficher les basiques"
          inline-prompt
        />
      </section>

      <!-- ================= Zone défilante (composants) ================= -->
      <div class="inv-scroll">
        <el-empty
          v-if="!loading && shipComponents.length === 0"
          description="Aucun composant libre. Gagne des combats ou fouille des chambres profondes pour récupérer des pièces de vaisseau."
        />
        <el-empty
          v-else-if="!loading && filteredComponents.length === 0"
          description="Aucun composant ne correspond à ces filtres."
        />

        <section
          v-for="g in groupedComponents"
          :key="g.category"
          class="inv-section"
        >
          <h2 class="inv-section-title">
            <el-tag type="info" effect="dark" size="default">
              {{ shipComponentCategoryLabel(g.category) }}
            </el-tag>
            <span class="inv-section-count text-muted">
              {{ g.items.length }} pièce{{ g.items.length > 1 ? 's' : '' }}
            </span>
          </h2>

          <div class="inv-grid">
            <ItemDisplay
              v-for="c in g.items"
              :key="c.id"
              :item="shipComponentToDisplayItem(c)"
              size="medium"
            />
          </div>
        </section>
      </div>

    </div>
    </el-tab-pane>
    </el-tabs>
  </div>
</template>

<style lang="scss" scoped>
.inventory-view {
  max-width: $layout-max-width;
  // Remplit la hauteur disponible (modale = corps à hauteur fixe). Le défilement
  // a lieu DANS les zones d'objets/composants, pas sur la modale entière.
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: $space-4;
  min-height: 0;
}

// ----- Header -------------------------------------------------------------
.inv-header {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: $space-3;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: $space-4;
  }
}

.inv-title {
  font-family: $font-display;
  font-size: $font-size-3xl;
  margin: 0 0 $space-1;
}

.inv-subtitle {
  margin: 0;
}

.inv-kind-summary {
  display: flex;
  flex-wrap: wrap;
  gap: $space-1;
  max-width: 100%;
}

.inv-kind-chip {
  &.is-empty { opacity: 0.45; }
}

// ----- Tabs : la zone de contenu prend la hauteur restante et défile ------
.inv-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  :deep(.el-tabs__content) {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
  :deep(.el-tab-pane) {
    height: 100%;
  }
}

// Volet d'un onglet : filtres figés en haut + zone défilante en dessous.
.inv-pane {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: $space-4;
  min-height: 0;
}

// Zone défilante : SEUL endroit qui scrolle (la modale, elle, ne bouge pas).
.inv-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: $space-5;          // espace net entre les sections de type (fix « Arme » collé)
  padding-right: 4px;     // évite que la scrollbar masque les cartes
}

// ----- Filtres ------------------------------------------------------------
.inv-filters {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-3;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 220px 220px;
  }
}

.inv-filter-search {
  width: 100%;
}

.inv-filter-select {
  width: 100%;
}

// ----- Sections par kind --------------------------------------------------
.inv-section {
  display: flex;
  flex-direction: column;
  gap: $space-3;
}

.inv-section-title {
  display: flex;
  align-items: center;
  gap: $space-3;
  margin: 0;
  font-family: $font-display;
  font-size: $font-size-xl;
}

.inv-section-count {
  font-size: $font-size-xs;
}

// ----- Grid + cards -------------------------------------------------------
.inv-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: $space-3;
}

// Conteneur d'un objet : permet de poser le badge « New » par-dessus la carte.
.inv-item {
  position: relative;
}
.inv-new-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 2;
  padding: 1px 7px;
  border-radius: 9px;
  background: $color-neon-cyan;
  color: #04222b;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  box-shadow: 0 0 8px rgba($color-neon-cyan, 0.6);
  pointer-events: none;
}
</style>
